/**
 * Zynix 셸에서 우리가 실제로 쓰는 초기화만 모은 것.
 *
 * 왜 이 파일이 있나
 * ---------------
 * Zynix 원본의 `js/custom.js` 는 **데모 페이지 전용** 스크립트다. `#loader`,
 * `#daterange`(flatpickr), `#header-search`(autoComplete), 색상 스위처 등
 * 우리가 쓰지 않는 요소와 라이브러리를 전제로 하고, **첫 줄에서 예외가 나면
 * 그 뒤 전부가 실행되지 않는다.** 그래서 콘솔에
 *   Uncaught ReferenceError: flatpickr is not defined
 *   Uncaught TypeError: Cannot read properties of null (reading 'classList')
 * 가 뜨면서 정작 필요한 것도 안 붙는 상태였다.
 *
 * 그 파일을 아예 로드하지 않고, 실제로 쓰던 것만 여기로 옮겼다.
 *   · 푸터 연도  → 서버 렌더로 옮김(JS 불필요, 깜빡임 없음)
 *   · Waves 리플 → 아래 initWaves()
 *
 * ★ 모든 초기화는 **있으면 붙이고 없으면 조용히 넘어간다.**
 *   라이브러리나 요소가 없다고 예외를 던지면 뒤따르는 초기화가 죽는다.
 */

/** node-waves 리플 효과. `.btn-wave` 클래스가 붙은 버튼에 적용된다. */
function initWaves(): void {
  // waves.min.js 는 shell_app.html 만 로드한다(auth_shell 에는 없다).
  const W = (window as unknown as { Waves?: { attach: (s: string, c?: string[]) => void; init: () => void } }).Waves;
  if (!W || typeof W.attach !== 'function') return;
  try {
    W.attach('.btn-wave', ['waves-light']);
    W.init();
  } catch {
    /* 리플은 장식이다 — 실패해도 화면에 영향이 없어야 한다 */
  }
}

/**
 * ★ F238(사장님 2026-08-07): **사이드바 안 닫기 버튼이 안 들었다.**
 *
 * 원인 — 벤더 `defaultmenu.min.js` 가 이렇게 쓴다:
 *
 *     headerToggleBtn = document.querySelector(".sidemenu-toggle");
 *     headerToggleBtn.addEventListener("click", toggleSidemenu);
 *
 * `querySelector` 는 **단수**다. 문서에 `.sidemenu-toggle` 이 둘 있으면
 * (헤더의 여는 버튼 + F227 로 사이드바 안에 넣은 닫는 버튼) **먼저 나온 하나만**
 * 배선된다. 그래서 열기는 되고 닫기는 안 됐다 — 마크업은 같은 부품인데
 * 벤더 배선이 하나뿐이라 생긴 일이다.
 *
 * 고침 — 벤더 파일은 재수입하면 사라지므로 **고치지 않는다.** 대신 벤더가
 * 배선한 첫 번째를 빼고 **나머지 전부**를 같은 전역 함수에 붙인다.
 * 전역 `toggleSidemenu` 가 없으면(벤더 미로딩) 조용히 넘어간다 —
 * 이 파일의 규칙이다.
 */
function bindExtraSidebarToggles(): void {
  const w = window as unknown as { toggleSidemenu?: () => void };
  const all = Array.from(document.querySelectorAll<HTMLElement>('.sidemenu-toggle'));
  // 벤더가 첫 번째 하나를 이미 잡았다. 그 하나에 또 붙이면 두 번 토글돼
  // 아무 일도 안 일어난 것처럼 보인다 — 반드시 **나머지만** 붙인다.
  for (const el of all.slice(1)) {
    if (el.dataset.nxSbBound === '1') continue;
    el.dataset.nxSbBound = '1';
    el.addEventListener('click', (ev: Event) => {
      ev.preventDefault();
      try {
        w.toggleSidemenu?.();
      } catch {
        /* 벤더가 없거나 죽었다 — 화면은 그대로 둔다 */
      }
    });
  }
}

/**
 * ★ F252(사장님 2026-08-07) — 벤더 `defaultmenu.min.js` 의 **좁은 화면 갈래**가
 *   우리 상태값을 모른다. 그 갈래는 이렇게 생겼다:
 *
 *     } else {                                   // innerWidth < 1025
 *       if (data-toggled === "close") {          // ← "close" **정확히** 일치
 *         set "open"; …오버레이를 만들고 .active 를 켠다
 *       } else {
 *         set "close";                           // ← 오버레이 .active 를 **안 끈다**
 *       }
 *     }
 *
 *   여기서 두 가지가 나온다 — 사장님이 둘 다 보셨다:
 *
 *   ① **첫 클릭이 안 듣는다.** 우리는 `closed` 모드를 쓰므로 부팅 뒤
 *      `data-toggled` 가 `close-menu-close` 이거나 **아예 없다**. 둘 다
 *      `"close"` 가 아니라서 첫 클릭은 그냥 `"close"` 로 **바꾸기만** 하고
 *      끝난다(화면 변화 없음). 두 번째 클릭에서야 열린다.
 *      → 좁은 화면에서는 상태를 미리 `"close"` 로 **정규화**해 둔다.
 *
 *   ② **닫아도 오버레이가 남는다.** 닫는 갈래가 `.active` 를 안 지운다.
 *      벤더는 오버레이를 **클릭**해서 닫을 때만 지운다(menuClose).
 *      → 토글을 누른 뒤 상태가 `open` 이 아니면 우리가 지운다.
 *
 *   벤더 파일은 재수입하면 사라지므로 고치지 않는다 — 여기서 감싼다.
 */
/* ★ F434b(사장님 재지시 2026-08-13): **셸 분기점 = 1025px**(목록 표는 1024px —
 *   일부러 1px 다르다. 1024 화면에서는 사이드바를 접고 표에 폭을 다 준다).
 *   벤더(styles.css·defaultmenu.min.js)도 zynix_import 의 patch_breakpoint()
 *   로 같은 값으로 맞춘다. 여기만 옛 값이면 그 1px 에서 «CSS 는 서랍인데
 *   JS 는 펼침» 이 된다. 정본 = zynix_import.SHELL_BREAKPOINT. */
const NARROW = '(max-width: 1024.98px)';

function normalizeToggleState(): void {
  if (!window.matchMedia(NARROW).matches) return;
  const html = document.documentElement;
  if (html.getAttribute('data-toggled') !== 'open') {
    // 벤더의 좁은 화면 갈래가 알아듣는 **유일한 닫힘 값**이다.
    html.setAttribute('data-toggled', 'close');
  }
}

/**
 * ★ F253·F254: 접힘/펼침을 **기억한다.** 전에는 페이지를 옮기면 늘 펼쳐진 채로
 *   돌아왔다(서버가 매번 같은 초기값을 그리므로).
 *   복원은 셸 `<head>` 의 짧은 스크립트가 한다(그려지기 전에 정해야 깜빡임이
 *   없다) — 여기서는 **저장만** 한다.
 *   좁은 화면은 덮개라 기억하지 않는다(늘 닫힌 채로 시작).
 */
function rememberSidebar(): void {
  try {
    if (window.innerWidth < 1025) return;   /* F434b: 셸 분기점 */
    const v = document.documentElement.getAttribute('data-toggled');
    const closed = v === 'close-menu-close';
    // ★ 0-①(2026-08-08): 첫 페인트 표식(data-nx-sb)도 같이 갱신한다 —
    //   CSS 가 접힘 복원을 이 표식으로 그리므로, 여기서 안 바꾸면 접힘
    //   복원 뒤 펼치기 클릭이 화면에 **영영** 안 듣는다(probe_f253 ④단계가
    //   잡는다). localStorage 보다 **먼저** 한다 — 저장이 막힌 환경에서도
    //   화면은 맞아야 한다(setItem 이 던지면 아래 줄은 안 돈다).
    document.documentElement.setAttribute('data-nx-sb', closed ? 'closed' : 'open');
    // F262-2: 세션 단위 기억 — localStorage 였다가 "며칠 전 접은 것이 기본이
    //   된다"는 지적으로 바꿨다(shell_app.html <head> 주석 참고).
    window.sessionStorage.setItem('nx.sidebar', closed ? 'closed' : 'open');
  } catch {
    /* 저장소 금지 환경 — 기억만 못 할 뿐 화면은 그대로 된다 */
  }
}

function syncResponsiveOverlay(): void {
  const on = document.documentElement.getAttribute('data-toggled') === 'open';
  const ov = document.getElementById('responsive-overlay');
  if (ov && !on) ov.classList.remove('active');
}

/**
 * ★ 0-①(2026-08-08): 좁은 화면을 다녀오면 data-toggled 가 'close'(서랍 닫힘)
 *   로 남는다. 넓은 화면에서 그 값은 "펼쳐짐"인데, 표식(data-nx-sb)이
 *   'closed' 면 CSS 는 접힘을 그린다 — 이때 벤더 closed-모드 토글의 첫
 *   클릭은 close-menu-close 로 **설정**(=접기)이라, 이미 접힌 화면에서
 *   죽은 클릭이 된다(F252 와 같은 병). 넓은 화면으로 돌아올 때 표식에 맞춰
 *   data-toggled 를 되돌려 상태와 값이 갈라지지 않게 한다.
 */
function resyncWideFromMarker(): void {
  const html = document.documentElement;
  if (html.getAttribute('data-nx-sb') === 'closed') {
    html.setAttribute('data-toggled', 'close-menu-close');
  } else {
    // closed 모드에서 "펼쳐짐"은 속성이 **없는** 상태다(함정 5).
    html.removeAttribute('data-toggled');
  }
  syncResponsiveOverlay();
}

function guardSidebarState(): void {
  normalizeToggleState();
  // 폭이 1025 를 넘나들면 같은 값의 뜻이 달라진다 — 그때마다 다시 맞춘다.
  try {
    window.matchMedia(NARROW).addEventListener('change', (e: MediaQueryListEvent) => {
      if (e.matches) { normalizeToggleState(); } else { resyncWideFromMarker(); }
    });
  } catch {
    /* 오래된 브라우저 — 초기 정규화만으로도 첫 클릭은 산다 */
  }
  // 토글은 여러 개다(헤더·사이드바 안). **위임**으로 한 번만 건다 —
  // 벤더 것이든 우리 것이든, 눌린 뒤 오버레이를 상태에 맞춘다.
  document.addEventListener('click', (ev: Event) => {
    const t = ev.target as HTMLElement | null;
    if (!t || !t.closest('.sidemenu-toggle')) return;
    // 벤더 처리기가 먼저 돌게 한 뒤에 본다(같은 클릭의 꼬리에서).
    window.setTimeout(() => { syncResponsiveOverlay(); rememberSidebar(); }, 0);
    // 오버레이는 100ms 뒤에 켜지므로 그 뒤에도 한 번 더 본다.
    window.setTimeout(() => { syncResponsiveOverlay(); rememberSidebar(); }, 160);
  }, true);
}

export function initZynixGlue(): void {
  // 9-107: Zynix defaultmenu.min.js 벤더 버그 방어 — 좁은 화면에서 사이드바를
  // 열 때 #responsive-overlay div 를 만들기만 하고 **DOM 에 붙이지 않아**
  // setTimeout 안의 querySelector 가 null 을 읽고 죽는다(사장님 콘솔 실측:
  // "Cannot read properties of null (reading 'classList')" @601). 벤더 파일은
  // 재수입하면 사라지므로 고치지 않고, 우리가 미리 만들어 둔다 — 벤더는
  // 이미 있으면 그대로 쓴다.
  if (!document.getElementById('responsive-overlay')) {
    const ov = document.createElement('div');
    ov.id = 'responsive-overlay';
    document.body.appendChild(ov);
  }
  guardSidebarState();
  bindExtraSidebarToggles();
  initWaves();
}
